/**
 * @fileoverview Cryptographic utilities for user ID encryption/decryption
 * Uses crypto-js for secure user ID handling to prevent attacks
 * @author ISCRI Team
 */

import CryptoJS from "crypto-js";

// Ensure the environment variable is defined
const ENCRYPTION_KEY = window._env_?.VITE_APP_ENCRYPTION_KEY;

if (!ENCRYPTION_KEY) {
  throw new Error(
    "Missing VITE_APP_ENCRYPTION_KEY — encryption cannot proceed."
  );
}

/**
 * Encrypt user ID to prevent exposure in client-side storage
 * @param {string|number} userId - Raw user ID to encrypt
 * @returns {string} Encrypted user ID string
 * @throws {Error} If encryption fails
 */
export const encryptUserId = (userId) => {
  try {
    if (!userId) {
      throw new Error("User ID is required for encryption");
    }

    const userIdString = String(userId);
    const encrypted = CryptoJS.AES.encrypt(
      userIdString,
      ENCRYPTION_KEY
    ).toString();

    return encrypted;
  } catch (error) {
    console.error("Failed to encrypt user ID:", error);
    throw new Error("Encryption failed");
  }
};

/**
 * Decrypt user ID from encrypted storage value
 * @param {string} encryptedUserId - Encrypted user ID string
 * @returns {string} Decrypted user ID
 * @throws {Error} If decryption fails
 */
export const decryptUserId = (encryptedUserId) => {
  try {
    if (!encryptedUserId) {
      throw new Error("Encrypted user ID is required for decryption");
    }

    const bytes = CryptoJS.AES.decrypt(encryptedUserId, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    if (!decrypted) {
      throw new Error("Invalid encrypted user ID or wrong key");
    }

    return decrypted;
  } catch (error) {
    console.error("Failed to decrypt user ID:", error);
    throw new Error("Decryption failed");
  }
};

/**
 * Generate secure session token for additional security layer
 * @returns {string} Random session token
 */
export const generateSessionToken = () => {
  const timestamp = Date.now().toString();
  const randomString = CryptoJS.lib.WordArray.random(16).toString();
  const combined = `${timestamp}-${randomString}`;

  return CryptoJS.SHA256(combined).toString();
};

/**
 * Validate encrypted user ID format
 * @param {string} encryptedUserId - Encrypted ID to validate
 * @returns {boolean} True if format is valid
 */
export const isValidEncryptedId = (encryptedUserId) => {
  if (!encryptedUserId || typeof encryptedUserId !== "string") {
    return false;
  }

  try {
    decryptUserId(encryptedUserId);
    return true;
  } catch {
    return false;
  }
};